import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Divider, 
  Paper, 
  Breadcrumbs,
  Link,
  IconButton
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderIcon from '@mui/icons-material/Folder';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import "./SFTPFiles.css";

function SFTPFiles({ SFTPItemID, SFTPName, SFTPFiles }) {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Get data based on SFTPItemID and current path
  useEffect(() => {
    setCurrentPath([]);
    setSelectedFile(null);
    
    if (SFTPItemID && SFTPFiles) {
      console.log("SFTPItemID:", SFTPItemID, "Type:", typeof SFTPItemID);
      getStaticData(SFTPItemID);
    }
  }, [SFTPItemID, SFTPFiles]);

  const getStaticData = (itemId, subFolder = null) => {
    console.log("Getting static data for itemId:", itemId, "subFolder:", subFolder);
    
    if (!SFTPFiles) {
      console.error(`No SFTPFiles data available for SFTP item ${itemId}`);
      setFiles([]);
      return;
    }
    
    if (subFolder === null) {
      // Get root level data
      setFiles(SFTPFiles.root || []);
    } else {
      // Get subfolder data
      if (SFTPFiles[subFolder]) {
        setFiles(SFTPFiles[subFolder]);
      } else {
        console.error(`No data found for subfolder ${subFolder} in SFTP item ${itemId}`);
        setFiles([]);
      }
    }
  };

  const handleFileClick = (file) => {
    if (file.type === 'folder') {
      // Navigate into the folder
      setCurrentPath([...currentPath, file.name]);
      getStaticData(SFTPItemID, file.name);
    } else {
      // Select the file (for future download functionality)
      setSelectedFile(file.name === selectedFile ? null : file.name);
      // Here you would add the download logic in the future
      console.log(`File selected for download: ${file.name}`);
    }
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      // Go back to root
      setCurrentPath([]);
      getStaticData(SFTPItemID);
    } else {
      // Go to specific folder in path
      const newPath = currentPath.slice(0, index + 1);
      setCurrentPath(newPath);
      getStaticData(SFTPItemID, newPath[newPath.length - 1]);
    }
  };

  const handleBackClick = () => {
    if (currentPath.length === 0) return;
    
    const newPath = currentPath.slice(0, -1);
    setCurrentPath(newPath);
    
    if (newPath.length === 0) {
      getStaticData(SFTPItemID);
    } else {
      getStaticData(SFTPItemID, newPath[newPath.length - 1]);
    }
  };

  // Render empty state if no SFTPItemID is selected
  if (!SFTPItemID && SFTPItemID !== 0) {
    return (
      <Box className="empty-state">
        <Typography variant="h6" color="textSecondary">
          Select an SFTP connection to view files
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb navigation */}
      {(currentPath.length > 0 || SFTPItemID === 4) && (
        <Paper className="breadcrumb-container">
          {currentPath.length > 0 && (
            <IconButton 
              size="small" 
              className="back-button"
              onClick={handleBackClick}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              className="breadcrumb-link"
              color="inherit"
              onClick={() => handleBreadcrumbClick(-1)}
            >
              {SFTPName || `SFTP Item ${SFTPItemID}`}
            </Link>
            
            {currentPath.map((folder, index) => (
              <Link
                key={folder}
                className="breadcrumb-link"
                color="inherit"
                onClick={() => handleBreadcrumbClick(index)}
              >
                {folder}
              </Link>
            ))}
          </Breadcrumbs>
        </Paper>
      )}
      
      {/* File list header */}
      <div className="file-list-header">
        <div className="file-list-header-item">Name</div>
        <div className="file-list-header-item">Last Modified</div>
        <div className="file-list-header-item">Size</div>
      </div>
      
      {/* File list */}
      <div className={`file-list-container ${(currentPath.length > 0 || SFTPItemID === 4) ? 'breadcrumb-visible' : ''}`}>
        {files.map((file) => (
          <div 
            key={file.name}
            className={`file-list-item ${selectedFile === file.name ? 'selected' : ''}`}
            onClick={() => handleFileClick(file)}
          >
            <div className="file-list-item-text">
              {file.type === 'folder' ? (
                <FolderIcon className="file-icon folder-icon" />
              ) : (
                <InsertDriveFileIcon className="file-icon csv" />
              )}
              {file.name}
            </div>
            <div className="file-list-item-text">{file.lastModified}</div>
            <div className="file-list-item-text">{file.size}</div>
          </div>
        ))}
      </div>
    </Box>
  );
}

export default SFTPFiles;
